import type { LinhaSimuladaResponse } from '@triade/contracts';
import { ApiError } from '@/lib/api';
import { lerIssue } from '@/lib/issues';
import { ROTULO_ALERTA, ROTULO_AVISO_LINHA, type LinhaFicha } from './tipos';

/**
 * Validacao da ficha AO TENTAR SALVAR (Sessao 37): a linha e o campo ficam marcados e uma lista diz "corrija: <campo> na linha N: <motivo>".
 * Enquanto o usuario edita nada disso aparece (so os avisos de custo da simulacao). Sem calculo: so junta o que a API disse (simulacao ou resposta de erro).
 */
export interface ErroLinhaFicha {
  uid: string;
  secao: LinhaFicha['secao'];
  /** Posicao (1-based) da linha dentro da secao (Processo ou Materiais), como o usuario a conta. */
  posicao: number;
  /** Colunas da grade a marcar (colId). */
  colunas: string[];
  /** Pares campo/motivo, um por problema. */
  itens: Array<{ campo: string; motivo: string }>;
}

const SECAO_ROTULO: Record<LinhaFicha['secao'], string> = { processo: 'Processo', materiais: 'Materiais' };

/** Campo da API (chave do JSON) -> coluna da grade e rotulo do campo. */
const CAMPO_API: Record<string, { col: string; rotulo: string }> = {
  descricao: { col: 'descricao', rotulo: 'Descrição' },
  componenteItemId: { col: 'descricao', rotulo: 'Componente' },
  quantidade: { col: 'quantidade', rotulo: 'Quantidade' },
  unidade: { col: 'unidade', rotulo: 'Unidade' },
  custoUnitario: { col: 'custoUnitario', rotulo: 'Custo unitário' },
  centroCustoId: { col: 'centro', rotulo: 'Centro analítico' },
  centroConsumoId: { col: 'centroConsumo', rotulo: 'Centro de consumo' },
  perdaValor: { col: 'perdaValor', rotulo: 'Valor da perda' },
  perdaModo: { col: 'perdaModo', rotulo: 'Perda' },
  tipo: { col: 'tipo', rotulo: 'Tipo' },
  fornecedorId: { col: 'fornecedor', rotulo: 'Fornecedor' },
};

/** Alerta/aviso/restricao da simulacao (que impede gravar) -> campo e motivo na tela. */
const DO_ALERTA: Record<string, { campo: string; col: string }> = {
  DESCRICAO_AUSENTE: { campo: 'Descrição', col: 'descricao' },
  QUANTIDADE_INVALIDA: { campo: 'Quantidade', col: 'quantidade' },
  CUSTO_AUSENTE: { campo: 'Custo unitário', col: 'custoUnitario' },
  CUSTO_INVALIDO: { campo: 'Custo unitário', col: 'custoUnitario' },
  PERDA_INVALIDA: { campo: 'Valor da perda', col: 'perdaValor' },
  CENTRO_INVALIDO: { campo: 'Centro', col: 'centro' },
  ETAPA_TIPO_INVALIDO: { campo: 'Tipo', col: 'tipo' },
  MAO_DE_OBRA_FORA_DO_PROCESSO: { campo: 'Tipo', col: 'tipo' },
  COMPONENTE_NAO_PERMITIDO: { campo: 'Componente', col: 'descricao' },
  CENTRO_CONSUMO_FORA_DAS_ETAPAS: { campo: 'Centro de consumo', col: 'centroConsumo' },
};
const MOTIVO_ALERTA: Record<string, string> = {
  ...(ROTULO_ALERTA as Record<string, string>),
  ...(ROTULO_AVISO_LINHA as Record<string, string>),
  MAO_DE_OBRA_FORA_DO_PROCESSO: 'Mão de obra do produto vai em Processo produtivo, com centro e minutos',
  COMPONENTE_NAO_PERMITIDO: 'Este item não compõe ficha',
  CENTRO_CONSUMO_FORA_DAS_ETAPAS: 'O centro de consumo não está nas etapas do processo desta ficha',
  ETAPA_TIPO_INVALIDO: 'Etapa só em mão de obra',
  CENTRO_INVALIDO: 'Escolha um centro analítico',
};
/** O que a simulacao ja sabe que a API recusaria ao gravar. */
const BLOQUEIA_AO_SALVAR = new Set(['QUANTIDADE_INVALIDA', 'CUSTO_AUSENTE', 'CUSTO_INVALIDO', 'DESCRICAO_AUSENTE', 'PERDA_INVALIDA', 'CENTRO_INVALIDO', 'ETAPA_TIPO_INVALIDO']);

/**
 * Erros de digitacao que NAO aparecem enquanto o usuario edita (nada de "linha nao pode ser gravada" no meio da edicao): so ao tentar salvar.
 * Os avisos de custo (sem CPM, sem aquisicao, CPM antigo...) seguem ao vivo.
 */
export const SO_AO_SALVAR: ReadonlySet<string> = new Set(['DESCRICAO_AUSENTE', 'QUANTIDADE_INVALIDA', 'CUSTO_INVALIDO', 'PERDA_INVALIDA', 'CENTRO_INVALIDO', 'ETAPA_TIPO_INVALIDO']);

function posicaoDe(linhas: readonly LinhaFicha[], indice: number): { uid: string; secao: LinhaFicha['secao']; posicao: number } | null {
  const l = linhas[indice];
  if (!l) return null;
  return { uid: l.uid, secao: l.secao, posicao: linhas.slice(0, indice + 1).filter((x) => x.secao === l.secao).length };
}

function acrescentar(mapa: Map<string, ErroLinhaFicha>, base: { uid: string; secao: LinhaFicha['secao']; posicao: number }, col: string, campo: string, motivo: string): void {
  const atual = mapa.get(base.uid) ?? { ...base, colunas: [], itens: [] };
  if (!atual.colunas.includes(col)) atual.colunas.push(col);
  if (!atual.itens.some((i) => i.campo === campo && i.motivo === motivo)) atual.itens.push({ campo, motivo });
  mapa.set(base.uid, atual);
}

const emOrdem = (m: Map<string, ErroLinhaFicha>, linhas: readonly LinhaFicha[]): ErroLinhaFicha[] => linhas.flatMap((l) => (m.has(l.uid) ? [m.get(l.uid) as ErroLinhaFicha] : []));

/** Problemas que a SIMULACAO ja mostra e que impediriam gravar (ao clicar em Salvar, sem chamar a API). */
export function errosDaSimulacao(linhas: readonly LinhaFicha[], calculo: ReadonlyMap<string, LinhaSimuladaResponse>): ErroLinhaFicha[] {
  const mapa = new Map<string, ErroLinhaFicha>();
  linhas.forEach((l, i) => {
    const sim = calculo.get(l.uid);
    const base = posicaoDe(linhas, i);
    if (!sim || !base) return;
    const codigos = [...sim.alertas, ...(sim.avisos ?? [])].filter((c) => BLOQUEIA_AO_SALVAR.has(c));
    for (const c of [...codigos, ...(sim.restricoes ?? [])]) {
      const alvo = DO_ALERTA[c];
      if (alvo) acrescentar(mapa, base, alvo.col, alvo.campo, MOTIVO_ALERTA[c] ?? c);
    }
  });
  return emOrdem(mapa, linhas);
}

/** Problemas na RESPOSTA de erro da API (400 Zod com `issues` e 422 com `details.linha`). Vazio se o erro nao aponta linha. */
export function errosDaResposta(linhas: readonly LinhaFicha[], erro: unknown): ErroLinhaFicha[] {
  if (!(erro instanceof ApiError)) return [];
  const mapa = new Map<string, ErroLinhaFicha>();
  for (const issue of erro.body?.issues ?? []) {
    const l = lerIssue(issue);
    if (l.linha === null) continue;
    const base = posicaoDe(linhas, l.linha - 1);
    if (!base) continue;
    const alvo = CAMPO_API[l.chave];
    acrescentar(mapa, base, alvo?.col ?? 'descricao', alvo?.rotulo ?? (l.campo || 'Linha'), l.motivo);
  }
  const det = (erro.body as { details?: { linha?: number; campo?: string; alertas?: string[]; restricoes?: string[] } } | null)?.details;
  if (det?.linha !== undefined) {
    const base = posicaoDe(linhas, det.linha - 1);
    if (base) {
      if (det.campo && CAMPO_API[det.campo]) acrescentar(mapa, base, CAMPO_API[det.campo]!.col, CAMPO_API[det.campo]!.rotulo, MOTIVO_ALERTA[erro.code ?? ''] ?? erro.message);
      const codigos = new Set([...(det.alertas ?? []).filter((c) => BLOQUEIA_AO_SALVAR.has(c)), ...(det.restricoes ?? []), ...(erro.code && DO_ALERTA[erro.code] ? [erro.code] : [])]);
      for (const c of codigos) {
        const alvo = DO_ALERTA[c];
        if (alvo) acrescentar(mapa, base, alvo.col, alvo.campo, MOTIVO_ALERTA[c] ?? c);
      }
      if (!mapa.has(base.uid)) acrescentar(mapa, base, 'descricao', 'Linha', erro.message);
    }
  }
  return emOrdem(mapa, linhas);
}

/** "Descrição na linha 2 (Materiais): Sem descrição" para cada problema; a tela antepoe "Corrija:". */
export function listaDeErros(erros: readonly ErroLinhaFicha[]): string[] {
  return erros.flatMap((e) => e.itens.map((i) => `${i.campo} na linha ${e.posicao} (${SECAO_ROTULO[e.secao]}): ${i.motivo}`));
}

/** Colunas marcadas por linha (uid), para as grades pintarem as celulas. */
export function colunasComErro(erros: readonly ErroLinhaFicha[]): ReadonlyMap<string, ReadonlySet<string>> {
  return new Map(erros.map((e) => [e.uid, new Set(e.colunas)]));
}
